import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Generic per-tab scoping for shared URL query params.
 *
 * Given a list of "scoped" param keys, this remembers their values per tab and swaps them
 * in and out of the URL as the active tab changes:
 *  - leaving a tab snapshots its current scoped param values in memory and strips them from
 *    the URL, so the tab being opened does not inherit the previous tab's filters;
 *  - entering a tab restores any values previously remembered for it.
 *
 * This lets sibling tabs reuse generic param names (e.g. "status", "search") without
 * cross-contaminating each other, while still preserving each tab's own filters when the
 * user navigates back to it.
 *
 * Memory lives for the lifetime of the mounted component (a ref), so it survives tab
 * switches but resets on a full page reload, where the URL re-seeds the active tab.
 *
 * Reusable anywhere multiple views share a query-param namespace behind tabs.
 */
export function useTabScopedSearchParams(scopedKeys: string[], tabParamKey = "tab") {
	const [, setSearchParams] = useSearchParams();
	// tabKey -> { paramKey -> value }
	const memoryRef = useRef<Record<string, Record<string, string>>>({});

	// Keep the latest keys without destabilising switchTab's identity.
	const scopedKeysRef = useRef(scopedKeys);
	useEffect(() => {
		scopedKeysRef.current = scopedKeys;
	}, [scopedKeys]);

	const switchTab = useCallback(
		(fromTabKey: string, toTabKey: string) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					const keys = scopedKeysRef.current;

					// Snapshot then strip the outgoing tab's scoped params.
					const snapshot: Record<string, string> = {};
					for (const key of keys) {
						const value = next.get(key);
						if (value !== null) {
							snapshot[key] = value;
						}
						next.delete(key);
					}
					memoryRef.current[fromTabKey] = snapshot;

					// Switch the tab param.
					next.set(tabParamKey, toTabKey);

					// Restore the incoming tab's remembered scoped params.
					const remembered = memoryRef.current[toTabKey];

					if (remembered) {
						for (const [key, value] of Object.entries(remembered)) {
							next.set(key, value);
						}
					}

					return next;
				},
				{ replace: true }
			);
		},
		[setSearchParams, tabParamKey]
	);

	return { switchTab };
}
